/**
 * Importing plain text (decisions 40, 64, 66; part 18 log, table TX, deep-fix r1): a `.txt` file is literal text, never
 * Markdown, as a Word file's text is. Every line is a paragraph (the user's choice, deep-fix r11: Word's and Scrivener's
 * plain text is a paragraph a line; a hand-wrapped file comes in a line a paragraph); a line that is only `#`, `***` or
 * `* * *` is a scene break, and a chapter's line a heading (TX4, TX5, TX6). Pure.
 */
import type { ImportedBook } from './chapters.ts';
import { draftChapters } from './chapters.ts';
import { FlagRecorder } from './flags.ts';
import { decodeMarkdown } from './markdown.ts';
import { normalizeSource, outputLimit } from '../literal/serialize.ts';
import { PLAIN_MARKS } from './write.ts';
import type { DraftBlock } from './write.ts';

/** TX3 (decision 64): the lines that are a scene break. */
const SCENE_BREAKS: ReadonlySet<string> = new Set(['#', '***', '* * *']);

/** A line that holds text, whether the line right above it holds text too (a wrap, part 18c), and how many lines under it do before a blank one. */
interface Line {
  readonly text: string;
  readonly wrapped: boolean;
  readonly continued: number;
}

/** TX1 (deep-fix r11, the user's choice "line = paragraph"): the file's paragraphs: every line that holds text, as written. */
function paragraphsOf(text: string): Line[] {
  const raw = text.split('\n');
  const filled = raw.map((line) => line.trim() !== '');
  const all = raw.map((line, i) => ({ text: line, wrapped: filled[i - 1] === true, continued: 0 }));
  // From the last line up, each counts the lines holding text under it before a blank one.
  let below = 0;
  for (const line of all.toReversed()) {
    line.continued = below;
    below = line.text.trim() === '' ? 0 : below + 1;
  }
  return all.filter((line) => line.text.trim() !== '');
}

/** Each item beside the one after it, in order. */
function pairs<T>(items: readonly T[]): (readonly [T, T])[] {
  return items.flatMap((item, n) => items.slice(n + 1, n + 2).map((next) => [item, next] as const));
}

/** TX4: the words that open a chapter's line, numbered after (any case; English and the languages writers here use). */
const NUMBERED = ['chapter', 'part', 'book', 'letter', 'kapittel', 'kapitel', 'del', 'teil', 'buch', 'chapitre', 'partie', 'livre', 'capítulo', 'capitulo', 'parte', 'libro', 'capitolo', 'hoofdstuk', 'deel', 'boek'];

/** TX4: the words that are a chapter's line on their own (a title may follow). */
const ALONE = ['prologue', 'epilogue', 'prolog', 'epilog', 'prólogo', 'epílogo', 'prologo', 'epilogo', 'proloog', 'epiloog', 'interlude', 'afterword', 'foreword', 'introduction'];

/** Part 18c, TX4: first to tenth in Spanish, Italian, French, Norwegian and Dutch, after the chapter word. */
const ORDINALS = [
  'primer[oa]', 'segund[oa]', 'tercer[oa]', 'cuart[oa]', 'quint[oa]', 'sext[oa]', 's[eé]ptim[oa]', 'octav[oa]', 'noven[oa]', 'd[eé]cim[oa]',
  'prim[oa]', 'terz[oa]', 'quart[oa]', 'sest[oa]', 'settim[oa]', 'ottav[oa]', 'non[oa]',
  'premi[eè]re?', 'deuxi[eè]me', 'seconde?', 'troisi[eè]me', 'quatri[eè]me', 'cinqui[eè]me', 'sixi[eè]me', 'septi[eè]me', 'huiti[eè]me', 'neuvi[eè]me', 'dixi[eè]me',
  'første', 'andre', 'tredje', 'fjerde', 'femte', 'sjette', 'sjuende', 'syvende', 'åttende', 'niende', 'tiende',
  'eerste', 'tweede', 'derde', 'vierde', 'vijfde', 'zesde', 'zevende', 'achtste', 'negende',
];

/**
 * TX4: a chapter's number: digits, a Roman numeral, an English number word (one to ninety-nine, first to ninetieth), or
 * (part 18c) an ordinal from first to tenth in the other languages {@link NUMBERED} holds ("Capítulo primero").
 */
const NUMBER = String.raw`(?:\d{1,4}|(?=[ivxlcdm])m{0,3}(?:cm|cd|d?c{0,3})(?:xc|xl|l?x{0,3})(?:ix|iv|v?i{0,3})(?![a-z])|(?:twenty|thirty|forty|fifty|sixty|seventy|eighty|ninety)(?:-(?:one|two|three|four|five|six|seven|eight|nine|first|second|third|fourth|fifth|sixth|seventh|eighth|ninth))?|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|thirteen|fourteen|fifteen|sixteen|seventeen|eighteen|nineteen|first|second|third|fourth|fifth|sixth|seventh|eighth|ninth|tenth|eleventh|twelfth|(?:thir|four|fif|six|seven|eigh|nine)teenth|(?:twen|thir|for|fif|six|seven|eigh|nine)tieth|${ORDINALS.join('|')})`;

/**
 * TX4 (the user's choice, deep-fix r4): a chapter's line: at most 60 characters, a numbered word and its number ("Chapter
 * 1", "CHAPTER ONE", "Part II") or a word that stands alone ("Prologue"), then nothing or a title after `:`, `.`, `–`, `—`
 * or `-`. A sentence that opens with such a word ("Part of me…", "Chapter one began…") isn't one.
 */
const CHAPTER_LINE = new RegExp(
  // A title after punctuation or a tab (Word's numbered heading saved as text: "Chapter 1<tab>The Storm", deep-fix r7), or
  // after a space when the number is in digits ("Chapter 1 The Storm"; after a word or a numeral a space starts a sentence).
  String.raw`^(?:(?:${NUMBERED.join('|')})\s+(?:\d{1,4}\.?(?:\s*[:.–—-]\s*\S.*|\t+\S.*| +\S.*)?|${NUMBER}\.?(?:\s*[:.–—-]\s*\S.*|\t+\S.*)?)|(?:${ALONE.join('|')})\.?(?:\s*[:.–—-]\s*\S.*|\t+\S.*)?)$`,
  'iu',
);

/** TX4: the most characters a chapter's line holds; {@link TITLED_LENGTH} for a numbered one with a title after punctuation. */
const LINE_LENGTH = 60;

/** Part 18c, TX4: a numbered chapter's line with its title after `:` `.` or a dash may run this long (a long chapter title). */
const TITLED_LENGTH = 100;

/** Part 18c, TX4: a numbered word, its number, then a title after punctuation. */
const TITLED = new RegExp(String.raw`^(?:${NUMBERED.join('|')})\s+(?:\d{1,4}|${NUMBER})\.?\s*[:.–—-]\s*\S`, 'iu');

/** A contents entry's page number after a tab, dot leaders or a gap (Word's plain-text table of contents). */
const PAGE_NUMBER = /(?:\t|\.{2,}|…| {2,})\s*\d+$/u;

/** TX4: whether `line` is a chapter's line on its own (a numeral alone needs its sequence: TX6). */
export function isChapterLine(line: string): boolean {
  const text = line.trim();
  const fits = text.length <= LINE_LENGTH || (text.length <= TITLED_LENGTH && TITLED.test(text));
  return fits && !PAGE_NUMBER.test(text) && CHAPTER_LINE.test(text);
}

/** TX6: an upper-case Roman numeral, I to MMMCMXCIX, as its value; anything else null. */
export function romanValue(numeral: string): number | null {
  if (!/^(?=[IVXLCDM])M{0,3}(?:CM|CD|D?C{0,3})(?:XC|XL|L?X{0,3})(?:IX|IV|V?I{0,3})$/u.test(numeral)) return null;
  // The pattern above admits only the seven ASCII letters, so a code unit is a letter: I, V, X, L, C, D, M at 0 to 6
  // are 1 or 5 times a power of ten.
  const values = Array.from(numeral, (letter) => {
    const at = 'IVXLCDM'.indexOf(letter);
    return (at % 2 === 0 ? 1 : 5) * 10 ** Math.floor(at / 2);
  });
  // From the right: a letter worth less than the one after it is taken off ("IV").
  let total = 0;
  let after = 0;
  for (const value of values.toReversed()) {
    total += value < after ? -value : value;
    after = value;
  }
  return total;
}

/** TX6: a line that is only an upper-case Roman numeral (a full stop after it allowed: "IV."). */
const BARE_NUMERAL = /^([IVXLCDM]+)\.?$/u;

/** TX6: the fewest numerals in a row (I, II, III, …) that make a book's chapter lines (N7). */
const SEQUENCE = 3;

/** TX6: a numeral line in `lines`: where, its value, and its form (N4). */
interface Numeral {
  readonly at: number;
  readonly value: number;
  readonly form: string;
}

/**
 * TX6 (deep-fix r2, r3; table TX6′ N3, N4): the candidate lines: a numeral alone that opens its block (no line right
 * above it), with no numeral right under it (a list, a contents list). Its form is its indent (none, or two spaces or
 * more) and its full stop: a book's chapter lines mostly share one.
 */
function candidatesOf(lines: readonly Line[]): Numeral[] {
  const parse = (line: Line): { value: number; form: string } | null => {
    const text = line.text.trim();
    const numeral = BARE_NUMERAL.exec(text)?.[1];
    const value = numeral === undefined ? null : romanValue(numeral);
    return value === null ? null : { value, form: `${/^\s{2,}/u.test(line.text) ? '>' : ''}${text.endsWith('.') ? '.' : ''}` };
  };
  const bare = lines.map((line) => parse(line) !== null);
  return lines.flatMap((line, i) => {
    const numeral = line.wrapped || bare[i + 1] === true ? null : parse(line);
    return numeral === null ? [] : [{ at: i, ...numeral }];
  });
}

/** TX6: a run of numerals being read: its numerals in order, its last one, its first line's form. */
interface Run {
  readonly numerals: [Numeral, ...Numeral[]];
  last: Numeral;
  readonly form: string;
}

/** TX5 (deep-fix r20): the numbered words of a book's higher level. */
const PART_WORDS: ReadonlySet<string> = new Set(['part', 'book', 'del', 'teil', 'buch', 'partie', 'livre', 'parte', 'libro', 'deel', 'boek']);

/** TX6 (deep-fix r1, r5): the numbered words whose line is a chapter Roman numerals under it are the sections of (N10). */
const PARENT_WORDS: ReadonlySet<string> = new Set(['chapter', 'kapittel', 'kapitel', 'chapitre', 'capítulo', 'capitulo', 'capitolo', 'hoofdstuk']);

/**
 * TX6 (part 18c, deep-fix r1–r14: table TX6′): the indexes of `lines` that are a Roman numeral's chapter line, in these
 * steps:
 * 1. Runs (N4–N6, N15): `I` opens a run, and any other numeral goes on the closest run that ends one below it (its last
 *    line latest), of its own form if one is open; numbering runs on across a part line or a letter, and a restart opens
 *    its own run. A step missing or repeated is stepped over (N15, r16: see the code).
 * 2. Sections under a chapter (N10): a run whose nearest numbered line above is a chapter word's ("Chapter One", not
 *    "Part One" or "Letter 2"), and not one of `contents` (TX5's entries, read with numerals alone as entries: r10), is
 *    text.
 * 3. A poem within (N13, r4, r15, r18, r22): a run whose lines all fall between two lines in a row of another run, of
 *    three or more and longer than it, that step by one, the second not that run's last, is text. A
 *    numbered poem or letter that ends the last chapter, or is numbered up to its own chapter's number, comes in as
 *    chapters of its own (N14 dropped after r14, the user's choice: a short extra chapter shows in the review, a
 *    merged one hides).
 * 4. Length (N7–N9): three or more, or two of a form a run of three has, or two whose next value a run of three or
 *    more took on after it (r21).
 */
function numeralLines(lines: readonly Line[], contents: ReadonlySet<number>, chapterLine: (i: number) => boolean): Set<number> {
  const runs: Run[] = [];
  const candidates = candidatesOf(lines);
  for (const [k, numeral] of candidates.entries()) {
    if (numeral.value === 1) runs.push({ numerals: [numeral], last: numeral, form: numeral.form });
    else {
      const byLast = (a: Run, b: Run): number => a.last.at - b.last.at;
      const ending = runs.filter(({ last }) => last.value === numeral.value - 1).sort(byLast);
      // r19: the form is preferred only among runs no restart has closed (none whose last line comes before the
      // closest one's first).
      const closest = ending.at(-1);
      const open = ending.filter((run) => closest === undefined || run.last.at >= closest.numerals[0].at);
      const exact = open.findLast(({ form }) => form === numeral.form) ?? closest;
      // N15 (r16, r17): a step missing, unreadable or repeated ("VIIII", "V" twice): when no run ends one below, the
      // run opened last, if it holds two or more of its form and ends two below or at this value, takes it. A jump of two waits for the
      // numeral after it: if the skipped value comes first (a letter signed "V." in chapter III), it isn't taken. (A
      // restart whose own step is missing, beside an earlier run that ends one below, is the same shape as a two-stanza
      // poem before the next chapter: the earlier run takes it.)
      const nextOf = candidates.slice(k + 1).find(({ value }) => value === numeral.value - 1 || value === numeral.value + 1);
      const jumps = nextOf?.value !== numeral.value - 1;
      // r18: only the run opened last steps over a step, never one a restart has closed.
      const latest = runs.at(-1);
      const near = latest !== undefined && latest.numerals.length >= 2 && latest.form === numeral.form && ((latest.last.value === numeral.value - 2 && jumps) || latest.last.value === numeral.value) ? latest : undefined;
      const run = exact ?? near;
      if (run !== undefined) {
        run.numerals.push(numeral);
        run.last = numeral;
      }
    }
  }
  // A numbered chapter line of a chapter word ("Chapter 3", not "Part Two" or "Letter 1").
  const chapterWord = (line: Line): boolean => {
    const text = line.text.trim();
    const [word = ''] = text.split(/\s/u);
    return ENTRY.test(text) && isChapterLine(text) && PARENT_WORDS.has(word.toLowerCase());
  };
  const numbered: { readonly at: number; readonly parent: boolean }[] = [];
  for (const [i, line] of lines.entries()) {
    const text = line.text.trim();
    // r10: a contents entry, as TX5 finds it before the numerals are read, is no parent.
    // r19, r20: a chapter line as TX4 takes it; a parent opens its own block (no line right above it).
    if (ENTRY.test(text) && chapterLine(i)) numbered.push({ at: i, parent: chapterWord(line) && !line.wrapped && !contents.has(i) });
  }
  const first = (run: Run): number => run.numerals[0].at;
  // Step 2.
  const outside = runs.filter((run) => numbered.findLast(({ at }) => at < first(run))?.parent !== true);
  // Step 3.
  // r15: only a run of three or more, longer than the one inside it, holds a poem (a preface's sections hide no novel).
  const within = (inner: Run): boolean =>
    outside.some(
      (outer) =>
        outer !== inner &&
        outer.numerals.length >= SEQUENCE &&
        outer.numerals.length > inner.numerals.length &&
        // r18: between two lines that step by one (a pair N15 joined, a repeat or a jump, holds nothing); r22: and not
        // before the outer run's last line (a signature "V." after a shorter later part holds no part).
        pairs(outer.numerals.slice(0, -1)).some(([above, under]) => above.at < first(inner) && inner.last.at < under.at && under.value === above.value + 1),
    );
  const kept = outside.filter((run) => !within(run));
  // Step 4. r21: a run whose numbering a run of three or more took on after it (a two-part poem in chapter II taking
  // III on) keeps its chapters, so the takeover hides none.
  const forms = new Set(kept.filter(({ numerals }) => numerals.length >= SEQUENCE).map(({ form }) => form));
  const continued = (run: Run): boolean =>
    run.numerals.length >= SEQUENCE - 1 &&
    kept.some((other) => other !== run && other.numerals.length >= SEQUENCE && other.numerals.some(({ value, at }) => value === run.last.value + 1 && at > run.last.at));
  return new Set(kept.filter((run) => run.numerals.length >= SEQUENCE || (run.numerals.length === SEQUENCE - 1 && forms.has(run.form)) || continued(run)).flatMap(({ numerals }) => numerals.map(({ at }) => at)));
}

/** TX5 (part 18c): a contents entry's start: a numbered word and its number, whatever follows. */
const ENTRY = new RegExp(String.raw`^(?:${NUMBERED.join('|')})\s+(?:\d{1,4}|${NUMBER})(?![\p{L}\p{N}])`, 'iu');

/** TX5: the most short lines between two entries of a contents list (an entry's title wrapped onto its own lines). */
const ENTRY_GAP = 2;

/**
 * TX5 (deep-fix r6, r7, the user's choice "numbers decide"): the indexes of `lines` that are a contents list's entries: a
 * run of two or more chapter lines, each after the one before but for at most {@link ENTRY_GAP} short lines (60
 * characters or fewer: a title wrapped onto its own line), where every entry's number comes back later as a chapter line
 * (the book's own chapters after its contents). A run whose numbers don't come back ("BOOK ONE", "PART I", "CHAPTER 1"
 * over prose) is the book's own. A number met again within a run ends it there. `back` marks the lines an entry may come
 * back as (the chapter lines unless given: deep-fix r10 also counts numerals alone, before TX6 has read them).
 */
function contentsEntries(lines: readonly Line[], chapter: readonly boolean[], back: readonly boolean[] = chapter): Set<number> {
  // Part 18c: an entry that is no chapter line ("CHAPTER IV OF WHAT HAPPENED", a title after a space) still holds the run.
  // Deep-fix r17: a sentence opening with the word ("Part two of the plan was simple.") holds none.
  const entry = (i: number, line: Line): boolean => chapter[i] === true || (ENTRY.test(line.text.trim()) && !/\p{Ll}.*[.!?…]$/u.test(line.text.trim()));
  const runs: (readonly { readonly at: number; readonly key: string }[])[] = [];
  let run: { at: number; key: string }[] = [];
  let seen = new Set<string>();
  let numbers = new Set<string>();
  const end = (): void => {
    if (run.length >= 2) runs.push(run);
    run = [];
    seen = new Set();
    numbers = new Set();
  };
  let gap = 0;
  for (const [i, line] of lines.entries()) {
    if (entry(i, line)) {
      const key = chapterKey(line.text);
      // Deep-fix r9: a numeral alone whose number the run has ("III" after "CHAPTER III") is met again too.
      if (seen.has(key) || numbers.has(key)) end();
      seen.add(key);
      numbers.add(key.split(' ')[1]?.toUpperCase() ?? '');
      run.push({ at: i, key });
      gap = 0;
      // Part 18c: an entry's title wrapped onto the line right under it, with no blank line between, may run long.
    } else if ((line.text.trim().length <= LINE_LENGTH || line.wrapped) && gap < ENTRY_GAP && run.length > 0) gap += 1;
    else end();
  }
  end();
  const out = new Set<number>();
  for (const entries of runs) {
    const after = Math.max(...entries.map((entry) => entry.at)) + 1;
    const later = new Set(lines.flatMap((line, i) => (i >= after && back[i] === true ? [chapterKey(line.text)] : [])));
    // Deep-fix r9: a chapter word's entry's Roman number comes back as a numeral alone too ("CHAPTER III", then "III");
    // a part's doesn't (r11: "PART II" would come back as its own chapter "II.").
    const comesBack = (key: string): boolean => {
      const [word = '', number = ''] = key.split(' ');
      return later.has(key) || (PARENT_WORDS.has(word) && later.has(number.toUpperCase()));
    };
    // Deep-fix r9: a word alone or a letter that doesn't come back as a chapter line ("Introduction", then "Introduction
    // to the Second Edition") doesn't sink a run whose numbered entries do.
    const soft = (key: string): boolean => ALONE.includes(key) || key.startsWith('letter ');
    // r12: two entries at least that come back; a numbered line beside a word alone that doesn't ("Chapter 2",
    // "Introduction") is no list.
    // r20: a part line right above its first chapter's line ("BOOK ONE", "I") is the body's, however an end table vouches.
    // r22: a part's numeral alone over its chapter "I" too.
    const [upper = '', under = ''] = entries.map(({ key }) => key);
    const [[upperWord = ''], [underWord = '']] = [upper.split(' '), under.split(' ')];
    const pair = entries.length === 2 && ((PART_WORDS.has(upperWord) && !PART_WORDS.has(underWord)) || (romanValue(upper) !== null && under === 'I'));
    if (!pair && entries.filter(({ key }) => comesBack(key)).length >= 2 && entries.every(({ key }) => comesBack(key) || soft(key))) for (const { at } of entries) out.add(at);
  }
  return out;
}

/** TX5: a chapter line's word and number, lower case ("chapter i" for "CHAPTER I. Down the Rabbit-Hole"). */
function chapterKey(line: string): string {
  // Deep-fix r8: a number word keeps its hyphen ("twenty-one"); a word that stands alone is its key, whatever title follows.
  const text = line.trim();
  const numeral = BARE_NUMERAL.exec(text)?.[1];
  // TX6: a numeral's line is keyed by its numeral, whatever title follows.
  if (numeral !== undefined && romanValue(numeral) !== null) return numeral;
  const [word = '', number = ''] = text.toLowerCase().split(/[\s.:–—]+|\s+-\s*/u);
  return ALONE.includes(word) ? word : `${word} ${number}`;
}

/** TX4 (part 18c): the most lines a long chapter title wraps onto under its first, before a blank line. */
const TITLE_WRAPS = 2;

/**
 * TX4 (part 18c, deep-fix r20): whether the chapter line `lines[i]`, over {@link LINE_LENGTH} characters, is a
 * hand-wrapped paragraph's first line instead: the line under it continues it (no blank line between) and runs long
 * too, and more than {@link TITLE_WRAPS} lines continue it, or a sentence ends inside the block after its title's
 * opening ("(Algerine Porpoise).—A pirate."), or its last line ends one with no word opening upper case ("and sent to
 * the printer."). A long title wraps onto a line or two ("Pictures of Whales.", "over Him."). A file of a paragraph a line with no
 * blank lines (Word's plain text) never takes a long one.
 */
function paragraphStart(lines: readonly Line[], i: number): boolean {
  const line = lines[i];
  const next = lines[i + 1];
  if (line === undefined || line.text.trim().length <= LINE_LENGTH || !next?.wrapped) return false;
  if (line.continued > TITLE_WRAPS) return next.text.trim().length > LINE_LENGTH;
  const block = lines.slice(i, i + line.continued + 1).map(({ text }) => text.trim());
  const rest = block.join(' ').replace(TITLED, '');
  const [last = ''] = block.slice(-1);
  // r21: a full stop after an abbreviation or an initial ("Mr. Pickwick"), or before a word in capitals ("THE STORM.
  // WHAT HAPPENED"), ends no sentence.
  const sentenceInside = [...rest.matchAll(/(\S*)[.!?]["”’)]*(?:\s+|—)(\S+)/gu)].some(([, before = '', after = '']) => {
    const word = before.replace(/[^\p{L}]/gu, '');
    return word.length > 1 && !ABBREVIATIONS.has(word.toLowerCase()) && /^\p{Lu}/u.test(after) && after !== after.toUpperCase();
  });
  return sentenceInside || (/[.!?]["”’)]*$/u.test(last) && !/(?:^|[\s(“‘"'])\p{Lu}/u.test(last));
}

/** TX4 (deep-fix r21): words a full stop follows without ending a sentence. */
const ABBREVIATIONS: ReadonlySet<string> = new Set(['mr', 'mrs', 'ms', 'dr', 'st', 'mt', 'jr', 'sr', 'messrs', 'rev', 'col', 'capt', 'gen', 'prof', 'hon', 'esq', 'vs', 'no']);

/** Table TX: the plain-text file `bytes` as a book, split at its chapters' lines (TX4), every line literal text. */
export function importText(bytes: Uint8Array): ImportedBook {
  const flags = new FlagRecorder();
  const text = normalizeSource(decodeMarkdown(bytes, flags));
  const lines = paragraphsOf(text);
  // Deep-fix r19: a line over 60 characters that continues the line above it is a hand-wrapped paragraph's, never a
  // chapter's ("…at the close of\nchapter 3. The rest of the book was written quickly, in a single autumn,").
  const long = (line: Line): boolean => line.wrapped && line.text.trim().length > LINE_LENGTH;
  const chapterLine = (i: number): boolean => {
    const line = lines[i];
    return line !== undefined && isChapterLine(line.text) && !long(line) && !paragraphStart(lines, i);
  };
  // Deep-fix r10: the contents entries before the numerals are read, a numeral alone counting as an entry's coming back.
  const plain = lines.map((line) => isChapterLine(line.text));
  const early = contentsEntries(lines, plain, lines.map((line, i) => plain[i] === true || romanValue(BARE_NUMERAL.exec(line.text.trim())?.[1] ?? '') !== null));
  const numerals = numeralLines(lines, early, chapterLine);
  const chapter = lines.map((_, i) => numerals.has(i) || chapterLine(i));
  const contents = contentsEntries(lines, chapter);
  // Deep-fix r7 (X6): a word alone or a letter's line right under a numbered chapter's line ("Chapter 1", then
  // "Introduction") is that chapter's title, text, and leaves the line above its heading.
  const follower = lines.map(({ text: line }) => {
    const [first = ''] = line.trim().split(/[\s.:–—]+|\s+-\s*/u);
    const word = first.toLowerCase();
    return ALONE.includes(word) || word === 'letter';
  });
  const entry = lines.map(({ text: line }) => ENTRY.test(line.trim()));
  const titleUnder = (i: number): boolean => chapter[i] === true && follower[i] === true && chapter[i - 1] === true && entry[i - 1] === true && follower[i - 1] !== true && !contents.has(i - 1);
  const blocks = lines.map(({ text: line }, i): DraftBlock => {
    if (SCENE_BREAKS.has(line.trim())) return { kind: 'scene-break' };
    const runs = [{ kind: 'text' as const, text: line, marks: PLAIN_MARKS, href: null }];
    // TX4: a chapter's line is a heading, so the book splits there (table C); one with nothing after it but another
    // chapter line, or the file's end, or one of a contents list (deep-fix r5, r6), stays text.
    const heading = chapter[i] === true && !contents.has(i) && !titleUnder(i) && i + 1 < lines.length && (chapter[i + 1] !== true || titleUnder(i + 1));
    return heading ? { kind: 'heading', level: 1, runs } : { kind: 'paragraph', runs };
  });
  const { chapters, chapterOf } = draftChapters(blocks, new Map(), flags, outputLimit(text.length));
  return { title: null, subtitle: null, chapters, flags: flags.flags(chapterOf) };
}
